import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import { InternalGuard } from '@/components/internal-guard';
// Eager: critical path (landing), auth flow, and DealPage (most-used /
// highest-value route — keeping it inline avoids a Suspense flash on the
// hot path).
import DealLayout from '@/pages/deal/deal-layout';
import { DealPage } from '@/pages/deal/page';
import HomePage from '@/pages/home';
import LoginPage from '@/pages/login';
import LogoutPage from '@/pages/logout';
import RedirectPage from '@/pages/redirect';
import { Loader2 } from 'lucide-react';

// Lazy: secondary routes loaded on navigation.
const SettingsPageLazy = lazy(() =>
  import('@/pages/deal/settings').then((m) => ({ default: m.SettingsPage }))
);
const HistoryPageLazy = lazy(() =>
  import('@/pages/deal/history').then((m) => ({ default: m.HistoryPage }))
);
const DealRedirect = lazy(() => import('@/pages/deal/deal-redirect'));
const NotFoundPage = lazy(() => import('@/pages/not-found'));

function RouteFallback() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );
}

export function AppRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/redirect" element={<RedirectPage />} />

        {/* Deal routes: shared Layout mounted once per dealId (keeps the SSE
            deal-items stream alive while navigating between tabs), + legacy
            redirects. */}
        <Route path="/deal/:dealId" element={<DealLayout />}>
          <Route index element={<DealPage />} />
          <Route path="settings" element={<SettingsPageLazy />} />
          <Route
            path="history"
            element={
              <InternalGuard>
                <HistoryPageLazy />
              </InternalGuard>
            }
          />
        </Route>
        <Route path="/deal/:dealId/*" element={<DealRedirect />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
